// askSmart-ui/src/pages/TOSP/taxBillIngest.ts
//
// Reads a municipal tax bill PDF into levies, one page per roll.
//
// The bill states what it is — "Interim Tax Bill  2026" in the title box, and
// again in the summary label and the instalment dates — so the type and year
// are read off the document rather than asked for. Uploading the Final under
// an "Add Interim" button would silently write ~$16.7k into the wrong column
// for C1 and nothing would error; detection can't make that mistake.
//
// Nothing is written from here. `planIngest` produces a summary the admin
// confirms first, because a bill that parsed wrongly is worse than one that
// wasn't uploaded.

import { TaxBillType, TaxLevy, TaxRollRecord } from "./types";

const PDFJS_WORKER_SRC =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";

async function loadPdfjs() {
  const pdfjs = await import(
    /* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf"
  );
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
  }
  return pdfjs;
}

/** 000-020-00440-0000. The "4242-" municipality prefix varies by town and is
 *  printed separately, so it stays out of the key. */
const ROLL_PATTERN = /\b(\d{3}-\d{3}-\d{5}-\d{4})\b/;
/**
 * Anchored: each run is its own item, so a partial match would be a bug.
 *
 * The minus can fall either side of the dollar sign — a credit prints as
 * "-$1.94". Reading that as no-match turned 96 credits into zeroes.
 */
const MONEY = /^(-)?\$?\s*(-)?([\d,]+\.\d{2})$/;
const DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
/** The Final writes its dates out: "August 21, 2025". */
const TEXT_DATE = /^([A-Z][a-z]+)\s+(\d{1,2}),\s*(\d{4})$/;
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * How far apart, in points, a label and its own figure may sit vertically.
 *
 * Measured off the real bill: the label and its amount are printed 0–2 points
 * apart, never on the exact same baseline — `TOTAL INTERIM TAX LEVY` is at
 * y=638 and its $1,409.93 at y=639. The next unrelated figure is 18 points
 * away, so 3 separates them cleanly with room to spare.
 */
const Y_TOLERANCE = 3;

const money = (text: string): number | null => {
  const match = text.trim().match(MONEY);
  if (!match) return null;
  const value = Number(match[3].replace(/,/g, ""));
  return match[1] || match[2] ? -value : value;
};

/** m/d/yyyy → yyyy-mm-dd, so dates sort and compare as strings. */
const isoDate = (text: string): string | null => {
  const match = text.trim().match(DATE);
  if (!match) return null;
  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

/** "August 21, 2025" → 2025-08-21. */
const isoTextDate = (text: string): string | null => {
  const match = text.trim().match(TEXT_DATE);
  if (!match) return null;
  const month = MONTH_NAMES.indexOf(match[1]);
  if (month < 0) return null;
  return `${match[3]}-${String(month + 1).padStart(2, "0")}-${match[2].padStart(2, "0")}`;
};

/** Either spelling — the Interim prints 8/21/2025, the Final August 21, 2025. */
const anyDate = (text: string): string | null => isoDate(text) || isoTextDate(text);

interface TextItem {
  str: string;
  transform: number[];
}

/** One run of text, with where it sits on the page. */
interface Placed {
  x: number;
  y: number;
  str: string;
}

const place = (items: TextItem[]): Placed[] =>
  items
    .filter((item) => item.str && item.str.trim())
    .map((item) => ({
      x: item.transform[4],
      y: item.transform[5],
      str: item.str.trim(),
    }));

/**
 * The figure belonging to a label — the nearest money to its right, on
 * roughly the same baseline.
 *
 * This is read by position rather than by joining text into lines, because the
 * bill is a form: pdf.js returns its runs in layout order, and a label and its
 * own amount are a point or two apart vertically. Bucketing by rounded y put
 * `TOTAL INTERIM TAX LEVY` and `$1,409.93` in different rows and found no
 * figures at all.
 */
function amountRightOf(items: Placed[], anchor: Placed): number | null {
  const candidates = items
    .filter(
      (item) =>
        Math.abs(item.y - anchor.y) <= Y_TOLERANCE &&
        item.x > anchor.x &&
        money(item.str) !== null,
    )
    .sort((a, b) => a.x - b.x);
  return candidates.length ? money(candidates[0].str) : null;
}

/** The text beside a label, e.g. the year printed next to the bill's title. */
function textRightOf(
  items: Placed[],
  anchor: Placed,
  pattern: RegExp,
): string | null {
  const match = items
    .filter(
      (item) =>
        Math.abs(item.y - anchor.y) <= Y_TOLERANCE &&
        item.x > anchor.x &&
        pattern.test(item.str),
    )
    .sort((a, b) => a.x - b.x)[0];
  return match ? match.str : null;
}

export interface ParsedLevy {
  rollNumber: string;
  /** What this bill charges — for a Final, the year's tax less the Interim. */
  levy: number;
  /** The year's whole tax, stated only on a Final. */
  annual?: number;
  pastDue: number;
  instalments: { dueDate: string; amount: number }[];
}

export interface ParsedBill {
  type: TaxBillType | null;
  year: number | null;
  /** As printed on the bill, e.g. "2026-02-20". */
  mailingDate: string;
  levies: ParsedLevy[];
  /** Pages carrying no roll number — a cover sheet, or a parse that failed. */
  skippedPages: number;
}

const TITLE = /\b(Interim|Final|Supplementary|Omitted)\s+Tax\s+Bill\b/i;
const LEVY_LABEL = /TOTAL\s+(INTERIM|FINAL|SUPPLEMENTARY)?\s*TAX\s+LEVY/i;
/** The summary line, not note 4 ("Past Due / -Credit reflects payments…"), so
 *  numbered notes are excluded. */
const PAST_DUE_LABEL = /PAST\s+DUE/i;

// ─── The Final's own labels ──────────────────────────────────────────────────
//
// The Final is a different document, not a variation on the Interim. It titles
// itself in three separate runs ("Tax Bill", "Final", "2025") instead of one,
// dates itself "Billing Date" rather than "Mailing Date", writes its dates out
// in words, and states the year's whole tax as "Final 2025 Levies" instead of
// a TOTAL … TAX LEVY line. Reading it with the Interim's rules found nothing
// at all on any of the 152 pages.

/** The year's whole tax. Also appears in Schedule 2's year-over-year table on
 *  the left, so the summary column's copy is the one taken. */
const FINAL_LEVY_LABEL = /^Final\s+(\d{4})\s+Levies$/i;
/** Where the summary column starts, in points. Schedule 2 sits left of it. */
const SUMMARY_COLUMN_X = 250;
/** What the Interim already took, and the marker that says this is a Final. */
const LESS_INTERIM_LABEL = /^Less\s+Interim\s+Billing$/i;
const FINAL_PAST_DUE_LABEL = /^Past\s+Due\s*\/\s*-?\s*Credit$/i;
/** Heads each payment stub at the foot of the page. */
const DUE_DATE_LABEL = /^Due\s+Date$/i;

/** The bill names itself in the title box; the summary label confirms it. */
function detectType(items: Placed[]): TaxBillType | null {
  const text = items.map((item) => item.str).join(" ").toLowerCase();

  // Interim first, and never on the words "final tax bill": the Interim's own
  // explanatory note reads "The Final Tax Bill mailed in July will reflect the
  // current tax rate", which would classify every Interim as a Final. Each
  // bill is identified by a phrase only it carries — the Interim by its title
  // and levy label, the Final by its Less Interim Billing line.
  if (/\binterim tax bill\b/.test(text) || /total interim tax levy/.test(text)) {
    return "interim";
  }
  if (
    /less interim billing/.test(text) ||
    /final\s+\d{4}\s+levies/.test(text) ||
    /total final tax levy/.test(text)
  ) {
    return "final";
  }
  if (/supplementary|omitted/.test(text)) return "supplementary";
  return null;
}

/** The year is printed beside the title, not inside it — "Interim Tax Bill"
 *  and "2026" are separate runs a point apart. The Final states it outright in
 *  its summary label, which is the surer read of the two. */
function detectYear(items: Placed[]): number | null {
  // Summary column only. Schedule 2 carries "Final 2024 Levies" for the
  // year-over-year comparison, and it can come first in the content stream —
  // taking either match would date the whole bill a year early.
  const finalLevy = items.find(
    (item) => item.x > SUMMARY_COLUMN_X && FINAL_LEVY_LABEL.test(item.str),
  );
  if (finalLevy) return Number(finalLevy.str.match(FINAL_LEVY_LABEL)![1]);

  const title = items.find((item) => TITLE.test(item.str));
  if (title) {
    const beside = textRightOf(items, title, /^20\d{2}$/);
    if (beside) return Number(beside);
  }
  const any = items.map((item) => item.str.match(/\b(20\d{2})\b/)).find(Boolean);
  return any ? Number(any[1]) : null;
}

/**
 * One page of a Final bill.
 *
 * What's recorded as the levy is what THIS bill charges — the year's whole tax
 * less what the Interim already took. So Interim + Final still adds up to the
 * year's tax, and Total PA stays the annual figure rather than counting the
 * first half twice. `annual` is carried alongside for the confirmation screen,
 * since "$281,819.70" on a bill headed $587,366.59 needs explaining.
 */
function parseFinalPage(items: Placed[]): ParsedLevy | null {
  const rollNumber = items.map((item) => item.str.match(ROLL_PATTERN)?.[1]).find(Boolean);
  if (!rollNumber) return null;

  // Prefer the summary column, but fall back to any copy that carries a figure
  // rather than failing outright on a page laid out slightly differently.
  const levyLabels = items.filter((item) => FINAL_LEVY_LABEL.test(item.str));
  const withAmount = levyLabels
    .map((label) => ({ label, amount: amountRightOf(items, label) }))
    .filter((found) => found.amount !== null);
  const annual =
    withAmount.find((found) => found.label.x > SUMMARY_COLUMN_X)?.amount ??
    withAmount[0]?.amount ??
    null;
  if (annual === null) return null;

  const lessLabel = items.find((item) => LESS_INTERIM_LABEL.test(item.str));
  const lessInterim = lessLabel ? amountRightOf(items, lessLabel) : null;
  if (lessInterim === null) return null;

  const pastLabel = items.find((item) => FINAL_PAST_DUE_LABEL.test(item.str));
  const pastDue = pastLabel ? amountRightOf(items, pastLabel) ?? 0 : 0;

  // Instalments come off the payment stubs, where the date and its amount sit
  // on one baseline. The Instalments panel higher up prints the amount ~12
  // points below its date and shares baselines with the summary column beside
  // it, so reading dates off the whole page picks up the wrong money.
  const seen = new Set<string>();
  const instalments: { dueDate: string; amount: number }[] = [];
  for (const label of items.filter((item) => DUE_DATE_LABEL.test(item.str))) {
    const dateItem = items
      .filter(
        (item) =>
          item.y < label.y &&
          label.y - item.y <= 30 &&
          Math.abs(item.x - label.x) <= 30 &&
          anyDate(item.str),
      )
      .sort((a, b) => b.y - a.y)[0];
    if (!dateItem) continue;
    const amount = amountRightOf(items, dateItem);
    if (amount === null) continue;
    const dueDate = anyDate(dateItem.str)!;
    const key = `${dueDate}:${amount}`;
    if (seen.has(key)) continue;
    seen.add(key);
    instalments.push({ dueDate, amount });
  }

  return {
    rollNumber,
    levy: Math.round((annual - lessInterim) * 100) / 100,
    annual,
    pastDue,
    instalments: instalments.sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
  };
}

function parseInterimPage(items: Placed[]): ParsedLevy | null {
  const rollNumber = items.map((item) => item.str.match(ROLL_PATTERN)?.[1]).find(Boolean);
  // Without a roll number the page can't be identified, so it's skipped rather
  // than half-imported against a guess.
  if (!rollNumber) return null;

  const levyLabel = items.find((item) => LEVY_LABEL.test(item.str));
  const levy = levyLabel ? amountRightOf(items, levyLabel) : null;
  if (levy === null) return null;

  const pastDueLabel = items.find(
    (item) => PAST_DUE_LABEL.test(item.str) && !/^\d+\./.test(item.str),
  );
  const pastDue = pastDueLabel ? amountRightOf(items, pastDueLabel) ?? 0 : 0;

  // Every date with a figure beside it. The payment stubs at the foot repeat
  // the instalment table, so identical date/amount pairs are dropped — and the
  // mailing date has no figure on its baseline, so it never qualifies.
  const seen = new Set<string>();
  const instalments: { dueDate: string; amount: number }[] = [];
  for (const item of items) {
    const dueDate = isoDate(item.str);
    if (!dueDate) continue;
    const amount = amountRightOf(items, item);
    if (amount === null) continue;
    const key = `${dueDate}:${amount}`;
    if (seen.has(key)) continue;
    seen.add(key);
    instalments.push({ dueDate, amount });
  }

  return {
    rollNumber,
    levy,
    pastDue,
    instalments: instalments.sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
  };
}

export async function readTaxBill(bytes: Uint8Array): Promise<ParsedBill> {
  const pdfjs = await loadPdfjs();
  // pdf.js takes ownership of the buffer it's handed, so it gets a copy.
  const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;

  const levies: ParsedLevy[] = [];
  let skippedPages = 0;
  let type: TaxBillType | null = null;
  let year: number | null = null;
  let mailingDate = "";

  for (let pageNo = 1; pageNo <= doc.numPages; pageNo += 1) {
    const page = await doc.getPage(pageNo);
    const content = await page.getTextContent();
    const items = place(content.items as TextItem[]);

    if (!type) type = detectType(items);
    if (!year) year = detectYear(items);
    if (!mailingDate) {
      // "Mailing Date" on the Interim, "Billing Date" on the Final, in either
      // spelling of a date.
      const label = items.find((item) => /(mailing|billing) date/i.test(item.str));
      const beside = label && textRightOf(items, label, /\d{4}$/);
      if (beside) mailingDate = anyDate(beside) || "";
    }

    const parsed = type === "final" ? parseFinalPage(items) : parseInterimPage(items);
    if (parsed) levies.push(parsed);
    else skippedPages += 1;
  }

  return { type, year, mailingDate, levies, skippedPages };
}

// ─── What the confirmation screen shows ──────────────────────────────────────

export interface IngestPlan {
  /** Rolls already in the register whose levy will be recorded or replaced. */
  matched: { roll: TaxRollRecord; parsed: ParsedLevy; previous?: TaxLevy }[];
  /** On the bill but not in the register — they need adding and assigning. */
  unknown: ParsedLevy[];
  /** In the register but absent from this bill — sold, severed, or merged. */
  missing: TaxRollRecord[];
  /** Levies that moved sharply against the same bill last year. */
  swings: { roll: TaxRollRecord; from: number; to: number; percent: number }[];
  /** Rolls carrying a prior-year balance the town has added to an instalment. */
  arrears: { roll: TaxRollRecord; amount: number }[];
  /** Sum of what this bill charges. */
  total: number;
  /** Sum of the year's whole tax — a Final only, where the two differ. */
  annualTotal: number | null;
  /** True when this bill has already been recorded — re-applying replaces it. */
  alreadyRecorded: boolean;
}

/** A levy moving more than this against last year is worth a second look. */
const SWING_THRESHOLD = 0.1;

/**
 * Works out what applying a bill would do, without doing any of it.
 *
 * Matching is on roll number — the municipality's own key — so re-uploading
 * the same bill replaces rather than duplicates, and a corrected reissue lands
 * on the rolls it belongs to.
 */
export function planIngest(
  register: TaxRollRecord[],
  bill: ParsedBill,
): IngestPlan {
  const { type, year } = bill;
  const byRollNumber = new Map(register.map((roll) => [roll.rollNumber, roll]));
  const seen = new Set<string>();

  const matched: IngestPlan["matched"] = [];
  const unknown: ParsedLevy[] = [];
  const swings: IngestPlan["swings"] = [];
  const arrears: IngestPlan["arrears"] = [];
  let total = 0;
  let annualTotal = 0;
  let alreadyRecorded = false;

  for (const parsed of bill.levies) {
    total += parsed.levy;
    annualTotal += parsed.annual ?? parsed.levy;
    const roll = byRollNumber.get(parsed.rollNumber);
    if (!roll) {
      unknown.push(parsed);
      continue;
    }
    seen.add(roll.id);

    const previous =
      type && year ? (roll.levies || []).find((l) => l.year === year && l.type === type) : undefined;
    if (previous) alreadyRecorded = true;

    matched.push({ roll, parsed, previous });

    if (parsed.pastDue > 0) arrears.push({ roll, amount: parsed.pastDue });

    // Compared against the SAME bill a year earlier — Interim against Interim.
    // Interim against Final would flag the whole portfolio every July.
    const lastYear =
      type && year ? (roll.levies || []).find((l) => l.year === year - 1 && l.type === type) : undefined;
    if (lastYear && lastYear.levy > 0) {
      const percent = (parsed.levy - lastYear.levy) / lastYear.levy;
      if (Math.abs(percent) > SWING_THRESHOLD) {
        swings.push({ roll, from: lastYear.levy, to: parsed.levy, percent });
      }
    }
  }

  return {
    matched,
    unknown,
    missing: register.filter((roll) => !seen.has(roll.id)),
    swings,
    arrears,
    total,
    // Only worth showing when it says something the total doesn't.
    annualTotal: Math.abs(annualTotal - total) > 0.01 ? annualTotal : null,
    alreadyRecorded,
  };
}

/** The rolls to write, with this bill's levy recorded on each. */
export function applyIngest(plan: IngestPlan, bill: ParsedBill): TaxRollRecord[] {
  const { type, year } = bill;
  if (!type || !year) return [];

  const label = `${year} ${type[0].toUpperCase()}${type.slice(1)}`;

  return plan.matched.map(({ roll, parsed }) => ({
    ...roll,
    lastBilled: label,
    levies: [
      // Same bill, same year replaces rather than accumulates, so uploading a
      // corrected reissue — or the same PDF twice — is safe.
      ...(roll.levies || []).filter((l) => !(l.year === year && l.type === type)),
      {
        type,
        year,
        levy: parsed.levy,
        pastDue: parsed.pastDue,
        instalments: parsed.instalments,
      },
    ],
  }));
}
